import React, { useEffect, useState, useMemo } from 'react';
import DocumentTitle from 'react-document-title';
import { connect } from 'react-redux';
import { Link, Route, Switch, useRouteMatch, useParams, useHistory, useLocation } from 'react-router-dom';
import { withTranslation } from 'react-i18next';

import { makeGetTeamToken } from 'selectors/auth';

import { permissionCheck } from 'api/global-api';
import { setPublicToken, getPublicToken } from 'services/public-share-service';
import { getUrlParams } from 'utils/index';

import ProjectRoadmap from 'lazy/LazyProjectRoadmap';
import TasksScreen from 'lazy/LazyTasksScreen';
import Document from 'lazy/LazyDocument';
import PublicForm from 'lazy/LazyPublicForm';

import Redirect from 'components/Redirect';
import PoweredBy from 'components/PoweredBy';

const PublicCTA = ({ branding, onboard, t }) => {
    return (
        <div className="public-page-overlay">
            {
                onboard ? (
                    <React.Fragment>
                        <Link to="/signup" className="btn btn-success">{ t('Sign up') }</Link>

                        <Link to="/login" className="btn btn-secondary">{ t('Log in') }</Link>
                    </React.Fragment>
                ) : null
            }

            {
                branding ? <PoweredBy /> : null
            }
        </div>
    );
}

const PublicPage = ({ type, teamToken, t }) => {
    const { publicToken, formId } = useParams();
    const match = useRouteMatch();
    const history = useHistory();
    const location = useLocation();
    const resourceMatch = useRouteMatch(`${match.url}/:type/:resourceId/:subresourceId?`);
    const [accessError, setAccessError] = useState(false);

    const computedType = useMemo(() => {
        return type || resourceMatch?.params?.type;
    }, [type, resourceMatch?.params?.type]);

    const urlParams = useMemo(() => {
        return getUrlParams(location?.search);
    }, [location?.search])

    const handleAccessError = () => {
        setAccessError(true);
    }

    const redirect = () => {
        setPublicToken(null);

        switch (computedType) {
            case 'document':
                history.replace(`/${resourceMatch?.params?.resourceId}/documents/root/${resourceMatch?.params?.subresourceId}`);
                break;

            case 'tasks':
                history.replace(`/${resourceMatch?.params?.resourceId}/task`);
                break;

            case 'milestones':
                history.replace(`/${resourceMatch?.params?.resourceId}/roadmap`);
                break;

            default:
                // es lint
        }
    }

    const onboardEnabled = useMemo(() => {
        return urlParams.onboard !== '0' && urlParams.onboard !== 'false';
    }, [urlParams.onboard]);

    const brandingEnabled = useMemo(() => {
        return urlParams.branding !== '0' && urlParams.branding !== 'false';
    }, [urlParams.branding]);

    const attemptRedirect = async teamToken => {
        let hasAccess = false;

        if (publicToken) {
            if (resourceMatch?.params?.resourceId) {
                if (computedType === 'document') {
                    if (resourceMatch?.params?.subresourceId) {
                        try {
                            const permissionCheckResult = await permissionCheck({ type: 'document', id: resourceMatch?.params?.subresourceId, token: teamToken });

                            hasAccess = permissionCheckResult.hasAccess;
                        } catch (e) {}
                    }

                } else {
                    try {
                        const permissionCheckResult = await permissionCheck({ type: 'project', id: resourceMatch.params.resourceId, token: teamToken });
                        hasAccess = permissionCheckResult.hasAccess;
                    } catch (e) {}

                }
            }

            if (hasAccess) {
                redirect();
            }
        }
    };

    if (getPublicToken() !== publicToken) {
        setPublicToken(publicToken);
    }

    useEffect(() => {
        if (teamToken) {
            attemptRedirect(teamToken);
        }
    }, [teamToken]);

    return (
        <DocumentTitle title="Nifty">
            <div className="app-inner">
                <div className="app-main">
                    <div className="wrapper">
                        <div className="main">
                            <div className="project-pane">
                                {
                                    accessError ? (
                                        <div className="maintenance">
                                            <div className="maintenance-inner">
                                                <p>{ t('errors:access.resource') }</p>

                                                <PublicCTA t={t} />
                                            </div>
                                        </div>
                                    ) : (
                                        <React.Fragment>
                                            {
                                                computedType === 'formPreview' && formId ? (
                                                    <PublicForm formId={formId} />
                                                ) : computedType === 'form' && publicToken ? (
                                                    <PublicForm publicToken={publicToken} />
                                                ) : publicToken ? (
                                                    <Switch>
                                                        <Route path={`${match.url}/tasks/:projectId`}>
                                                            <TasksScreen onAccessError={handleAccessError} tasksScreenType="public" projectId={resourceMatch?.params?.resourceId} />
                                                        </Route>

                                                        <Route path={`${match.url}/milestones/:projectId`}>
                                                            <ProjectRoadmap isPublic={true} />
                                                        </Route>

                                                        <Route path={`${match.url}/document/:projectId/:documentId/:pageId?`}>
                                                            <Document isPublic={true} />
                                                        </Route>

                                                        <Redirect strict from={match.url} to="/" />
                                                    </Switch>
                                                ) : <Redirect to="/" />
                                            }
                                        </React.Fragment>
                                    )
                                }
                            </div>
                        </div>
                    </div>

                    {
                        !accessError ? (
                            <div className="public-page-overlay-floating">
                                <PublicCTA
                                    branding={brandingEnabled}
                                    onboard={['form', 'formPreview'].includes(computedType) ? false : onboardEnabled}
                                    t={t}
                                />
                            </div>
                        ) : null
                    }
                </div>
            </div>
        </DocumentTitle>
    )
}

const makeMapStateToProps = () => {
    const getTeamToken = makeGetTeamToken();

    return ({ auth }) => ({
        teamToken: getTeamToken(auth)
    });
};

export default connect(makeMapStateToProps)(withTranslation(['onboarding', 'errors'])(PublicPage));
